/**
 * persistence/crypto/fieldCipher.js
 *
 * Transparent at-rest obfuscation for the analyst string data kept in
 * IndexedDB.  Encrypts string / JSON / binary fields with a default key baked
 * into the frontend bundle so the data is not human-readable in DevTools.
 *
 * ⚠️ THREAT MODEL — this is obfuscation, not real cryptographic security.
 * The key ships in the client bundle, so anyone determined enough can recover
 * it and decrypt the data. It only stops casual reading of IndexedDB. Real
 * protection must come from the server (short-lived tokens, auth-scoped APIs).
 *
 * Why a synchronous cipher (not Web Crypto AES)?
 *   Dexie's reading/creating/updating hooks must run synchronously, and
 *   crypto.subtle is async-only. We therefore use a keyed XOR stream cipher
 *   with a per-value random nonce (so equal plaintexts don't produce equal
 *   ciphertexts). Adequate for the obfuscation goal above.
 *
 * Backward compatibility: encrypted values carry a marker. Reads of legacy
 * plaintext rows (written before this shipped) are returned unchanged, so no
 * migration is required — old rows simply re-encrypt on the next write.
 */

// Default key — intentionally in frontend code, per design.
const DEFAULT_KEY = "stepout-idb-v1-7Qx2pL9fZ";

const STR_MARKER = "E1:"; // prefix on encrypted strings
const BYTES_MAGIC = [0x53, 0x4f, 0x45, 0x31]; // "SOE1" header on encrypted blobs

// ─── keystream ──────────────────────────────────────────────────────────────

function keySeed(key) {
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

const SEED = keySeed(DEFAULT_KEY);

function mulberry32(a) {
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** XOR `bytes` (Uint8Array) in place with a keystream seeded by SEED^nonce. */
function xorStream(bytes, nonce) {
  const rand = mulberry32((SEED ^ nonce) >>> 0);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] ^= Math.floor(rand() * 256);
  }
  return bytes;
}

function randNonce() {
  // App-side randomness is fine here (not a workflow script).
  return (Math.random() * 0xffffffff) >>> 0;
}

// ─── base64 (browser) ─────────────────────────────────────────────────────────

function bytesToB64(bytes) {
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}
function b64ToBytes(b64) {
  const s = atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

// ─── string fields ────────────────────────────────────────────────────────────

export function encStr(value) {
  if (typeof value !== "string" || value.startsWith(STR_MARKER)) return value;
  const nonce = randNonce();
  const data = new TextEncoder().encode(value);
  xorStream(data, nonce);
  const nb = new Uint8Array(4);
  new DataView(nb.buffer).setUint32(0, nonce);
  const combined = new Uint8Array(nb.length + data.length);
  combined.set(nb, 0);
  combined.set(data, nb.length);
  return STR_MARKER + bytesToB64(combined);
}

export function decStr(value) {
  if (typeof value !== "string" || !value.startsWith(STR_MARKER)) return value;
  try {
    const combined = b64ToBytes(value.slice(STR_MARKER.length));
    const nonce = new DataView(combined.buffer).getUint32(0);
    const data = combined.slice(4);
    xorStream(data, nonce);
    return new TextDecoder().decode(data);
  } catch {
    return value; // corrupt — return raw rather than throw
  }
}

// ─── binary fields (Uint8Array, e.g. Yjs blobs) ────────────────────────────────

function hasMagic(bytes) {
  return (
    bytes &&
    bytes.length >= 8 &&
    BYTES_MAGIC.every((b, i) => bytes[i] === b)
  );
}

export function encBytes(value) {
  if (!(value instanceof Uint8Array) || hasMagic(value)) return value;
  const nonce = randNonce();
  const data = value.slice();
  xorStream(data, nonce);
  const out = new Uint8Array(8 + data.length);
  out.set(BYTES_MAGIC, 0);
  new DataView(out.buffer).setUint32(4, nonce);
  out.set(data, 8);
  return out;
}

export function decBytes(value) {
  if (!(value instanceof Uint8Array) || !hasMagic(value)) return value;
  const nonce = new DataView(value.buffer, value.byteOffset).getUint32(4);
  const data = value.slice(8);
  return xorStream(data, nonce);
}

// ─── JSON fields (objects / arrays) ─────────────────────────────────────────────

export function encJSON(value) {
  if (value == null || typeof value === "string") return encStr(value);
  return encStr(JSON.stringify(value));
}

export function decJSON(value) {
  if (typeof value !== "string") return value; // legacy plaintext object
  const plain = decStr(value);
  if (plain === value) return value; // wasn't encrypted
  try {
    return JSON.parse(plain);
  } catch {
    return plain;
  }
}
